import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
  Spinner,
  Text,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  Input,
  useToast,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import FileViewerModal from "App/BsesPoc/components/common/ViewerModal/FileViewerModel";
import { ViewIcon, AttachmentIcon } from "@chakra-ui/icons";

type ForeignBank = {
  _id?: string;
  companyCode: any;
  vendorCode: string;
  beneficiaryName: string;
  beneficiaryBank: string;
  accountNo: string;
  swiftCode: string;
  routingNumber?: string;
  abaNumber?: string;
  ibanNumber?: string;
  correspondingBankName?: string;
  correspondingAccountNo?: string;
  correspondingSwiftCode?: string;
  correspondingRoutingNumber?: string;
  beneficiaryAddress: string;
  country: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  file?: any;
};

const COMPANY_CODES = [
  "EBL", "EMX", "ENL", "ELPL", "ENZ", "EPC", "ESA", "EPE", "EPP", "EWPL", "EAU", "EPL", "EMN", "EPDR", "EUK", "EBT", "EMCT", "EUS", "AVL", "LPK", "MNPI", "MPI", "TII", "TIL", "TIM", "ZHL",
];

const initialForm: ForeignBank = {
  companyCode: "",
  vendorCode: "",
  beneficiaryName: "",
  beneficiaryBank: "",
  accountNo: "",
  swiftCode: "",
  routingNumber: "",
  abaNumber: "",
  ibanNumber: "",
  correspondingBankName: "",
  correspondingAccountNo: "",
  correspondingSwiftCode: "",
  correspondingRoutingNumber: "",
  beneficiaryAddress: "",
  country: "",
};

export default function ForeignBankScreen(): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [form, setForm] = useState<ForeignBank>(initialForm);
  const [list, setList] = useState<ForeignBank[]>([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchVendorCode, setSearchVendorCode] = useState("");
  const [searchBeneficiaryName, setSearchBeneficiaryName] = useState("");
  const [searchCountry, setSearchCountry] = useState("");
  const [searchCompanyCode, setSearchCompanyCode] = useState("");

  const [debouncedVendorCode, setDebouncedVendorCode] = useState("");
  const [debouncedBeneficiaryName, setDebouncedBeneficiaryName] = useState("");
  const [debouncedCountry, setDebouncedCountry] = useState("");
  const [debouncedCompanyCode, setDebouncedCompanyCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Vendor Search States
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [showVendorList, setShowVendorList] = useState(false);
  const [isVendorLoading, setIsVendorLoading] = useState(false);
  const [isVendorSearching, setIsVendorSearching] = useState(false);

  const [selectedFile, setSelectedFile] = useState<{ file: any; open: boolean; localUrl: string; localType: string; }>({ file: null, open: false, localUrl: "", localType: "" });
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<string | null>(null);

  const toast = useToast();

  const { isOpen, onOpen, onClose: onDisclosureClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const handleClose = () => {
    setForm(initialForm);
    setEditingId(undefined);
    setDocumentFile(null);
    if (documentUrl) URL.revokeObjectURL(documentUrl);
    setDocumentUrl(null);
    setDocumentType(null);
    setIsViewOnly(false);
    onDisclosureClose();
  };

  const [deleteTargetId, setDeleteTargetId] = useState<string | undefined>(undefined);

  const handleChange = (name: keyof ForeignBank, value: any) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fetchForeignBanks = async () => {
    try {
      setLoading(true);
      const res: any = await appRequest("foreignBank", "getAll", {
        page,
        limit,
        vendorCode: debouncedVendorCode || undefined,
        beneficiaryName: debouncedBeneficiaryName || undefined,
        country: debouncedCountry || undefined,
        companyCode: debouncedCompanyCode || undefined,
      });

      if (res?.status === "success") {
        setList(res?.data?.data || []);
        setTotalPages(res?.data?.totalPages || 1);
        setTotalCount(res?.data?.totalCount || 0);
      }
    } catch (err: any) {
      toastNotify({
        title: "Fetch failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedVendorCode(searchVendorCode);
      setDebouncedBeneficiaryName(searchBeneficiaryName);
      setDebouncedCountry(searchCountry);
      setDebouncedCompanyCode(searchCompanyCode);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchVendorCode, searchBeneficiaryName, searchCountry, searchCompanyCode]);

  useEffect(() => {
    fetchForeignBanks();
  }, [page, limit, debouncedVendorCode, debouncedBeneficiaryName, debouncedCountry, debouncedCompanyCode]);

  const handleVendorSearchChange = (value: string) => {
    setVendorSearch(value);
    setForm((prev) => ({ ...prev, beneficiaryName: value, vendorCode: "" }));

    if (!value || value.length < 3 || !form.companyCode) {
      setVendorList([]);
      setShowVendorList(false);
      return;
    }
  };

  const handleVendorSearch = async () => {
    if (!vendorSearch?.trim() || !form.companyCode) {
      toastNotify({
        title: "Search criteria missing",
        message: "Please select a company and enter vendor name/code",
        type: "warning",
      });
      return;
    }

    setIsVendorSearching(true);
    try {
      const res: any = await appRequest("user", "masterFinancialApi", {
        type: "getVendorByName",
        vendorName: vendorSearch.trim(),
        companyCode: form.companyCode?.value || form.companyCode,
      });

      if (res?.status === "success" && Array.isArray(res.data)) {
        const validVendors = res.data.filter((v: any) => v.vendorCode && v.supplierName);
        setVendorList(validVendors);
        setShowVendorList(validVendors.length > 0);
        if (validVendors.length === 0) {
          toastNotify({ title: "Not Found", message: "No vendors matched your search in SAP", type: "info" });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsVendorSearching(false);
    }
  };

  const selectVendor = (vendor: any) => {
    setForm((prev) => ({
      ...prev,
      beneficiaryName: vendor.supplierName,
      vendorCode: vendor.vendorCode,
      beneficiaryAddress: vendor.vendorAddress || prev.beneficiaryAddress,
    }));
    setVendorSearch(vendor.supplierName);
    setShowVendorList(false);
  };

  useEffect(() => {
    if (!isOpen) {
      setDocumentFile(null);
      if (documentUrl) URL.revokeObjectURL(documentUrl);
      setDocumentUrl(null);
      setDocumentType(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setPage(1);
  }, [debouncedVendorCode, debouncedBeneficiaryName, debouncedCountry, debouncedCompanyCode]);

  const handleAdd = async () => {
    if (isViewOnly) {
      handleClose();
      return;
    }

    const requiredFields: (keyof ForeignBank)[] = [
      "companyCode",
      "vendorCode",
      "beneficiaryName",
      "beneficiaryBank",
      "accountNo",
      "swiftCode",
      "beneficiaryAddress",
      "country",
    ];

    for (const field of requiredFields) {
      if (!form[field]) {
        toastNotify({
          title: "Validation error",
          message: `${(field as string).replace(/([A-Z])/g, " $1").trim()} is required`,
          type: "error",
        });
        return;
      }
    }

    try {
      setLoading(true);

      // Handle file conversion to base64
      let base64File: any = undefined;
      if (documentFile) {
        const reader = new FileReader();
        base64File = await new Promise((resolve) => {
          reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            resolve({
              file: base64,
              filename: documentFile.name,
              type: documentFile.type,
            });
          };
          reader.readAsDataURL(documentFile);
        });
      }

      let res: any;
      if (editingId) {
        res = await appRequest("foreignBank", "update", {
          id: editingId,
          ...form,
          companyCode: form.companyCode?.value || form.companyCode,
          file: base64File,
        });
      } else {
        res = await appRequest("foreignBank", "create", {
          ...form,
          companyCode: form.companyCode?.value || form.companyCode,
          file: base64File,
        });
      }

      if (res?.status === "success") {
        toastNotify({
          title: "Success",
          message: editingId ? "Updated successfully" : "Saved successfully",
          type: "success",
        });

        setForm(initialForm);
        setEditingId(undefined);
        fetchForeignBanks();
        handleClose();
      } else {
        toastNotify({
          title: "Operation failed",
          message: res?.message || "Validation error from server",
          type: "error",
        });
      }
    } catch (err: any) {
      toastNotify({
        title: "Operation failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setLoading(true);
      await appRequest("foreignBank", "delete", { id: deleteTargetId });

      toastNotify({
        title: "Deleted",
        message: "Bank details removed successfully",
        type: "success",
      });

      fetchForeignBanks();
      onDeleteClose();
    } catch (err: any) {
      toastNotify({
        title: "Delete failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ShadowBox>
      <Flex mb={4} align="center" justify="space-between">
        <Box>
          <Text fontWeight="bold" fontSize="2xl" bgGradient="linear(to-r, teal.600, green.400)" bgClip="text" letterSpacing="tight">
            Foreign Bank Master
          </Text>
          <Text fontSize="sm" color="gray.500" fontWeight="500">Official vendor bank registry for international remittances</Text>
        </Box>

        <Button
          leftIcon={<span>+</span>}
          colorScheme="teal"
          variant="solid"
          borderRadius="full"
          px={10}
          h="45px"
          boxShadow="0 4px 14px 0 rgba(49, 151, 149, 0.39)"
          _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(49, 151, 149, 0.23)" }}
          onClick={() => {
            setForm(initialForm);
            setEditingId(undefined);
            setIsViewOnly(false);
            onOpen();
          }}
        >
          Add Bank Details
        </Button>
      </Flex>

      <HStack spacing={6} mb={8} bg="gray.50" p={5} borderRadius="20px" border="1px" borderColor="gray.100">
        <CustomInput
          name="searchVendorCode"
          label="Vendor Code"
          value={searchVendorCode}
          onChange={(e: any) => setSearchVendorCode(e.target.value)}
        />
        {/* <CustomInput
          name="searchBeneficiaryName"
          label="Beneficiary Name"
          value={searchBeneficiaryName}
          onChange={(e: any) => setSearchBeneficiaryName(e.target.value)}
        /> */}
        {/* <CustomInput
          name="searchCountry"
          label="Country"
          value={searchCountry}
          onChange={(e: any) => setSearchCountry(e.target.value)}
        />
        <CustomInput
          name="searchCompanyCode"
          label="Company Code"
          value={searchCompanyCode}
          onChange={(e: any) => setSearchCompanyCode(e.target.value)}
        /> */}
      </HStack>

      <Box mt={3} maxH="65vh" overflowY="auto" borderRadius="20px" border="1px" borderColor="gray.100" boxShadow="sm">
        {loading && list.length === 0 ? (
          <Flex justify="center" p={20}>
            <Spinner color="teal.500" size="xl" thickness="4px" />
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">COMPANY</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">VENDOR CODE</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">BENEFICIARY NAME</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">BENEFICIARY BANK</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">ACCOUNT NO</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">SWIFT CODE</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600">COUNTRY</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600" textAlign="center">DOCUMENT</Th>
                <Th py={5} fontSize="xs" fontWeight="700" color="gray.600" textAlign="center">ACTION</Th>
              </Tr>
            </Thead>

            <Tbody>
              {list.map((item) => (
                <Tr key={item._id} _hover={{ bg: "teal.50" }} transition="0.2s">
                  <Td>
                    <Badge colorScheme="purple" variant="subtle" px={2} py={1} borderRadius="md" fontWeight="bold">
                      {item.companyCode || "N/A"}
                    </Badge>
                  </Td>
                  <Td fontWeight="600" color="teal.700">{item.vendorCode}</Td>
                  <Td fontWeight="500">{item.beneficiaryName}</Td>
                  <Td>{item.beneficiaryBank}</Td>
                  <Td fontFamily="mono" fontSize="xs" fontWeight="600">{item.accountNo}</Td>
                  <Td fontFamily="mono" fontSize="xs">{item.swiftCode}</Td>
                  <Td>
                    <Badge colorScheme="teal" variant="subtle" borderRadius="full" px={3} py={0.5}>
                      {item.country}
                    </Badge>
                  </Td>
                  <Td textAlign="center">
                    {item.file ? (
                      <IconButton
                        aria-label="View Document"
                        icon={<AttachmentIcon />}
                        size="xs"
                        colorScheme="purple"
                        variant="ghost"
                        onClick={() => setSelectedFile({ open: true, file: item.file, localUrl: "", localType: "" })}
                      />
                    ) : (
                      <Text fontSize="xs" color="gray.300">—</Text>
                    )}
                  </Td>
                  <Td>
                    <HStack justify="center" spacing={3}>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="teal"
                        borderRadius="full"
                        onClick={() => {
                          const formattedCompany = item.companyCode ? { label: item.companyCode, value: item.companyCode } : "";
                          setForm({ ...item, companyCode: formattedCompany as any });
                          setEditingId(item._id);
                          setIsViewOnly(true);
                          onOpen();
                        }}
                      >
                        View
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        borderRadius="full"
                        onClick={() => {
                          const formattedCompany = item.companyCode ? { label: item.companyCode, value: item.companyCode } : "";
                          setForm({ ...item, companyCode: formattedCompany as any });
                          setEditingId(item._id);
                          setIsViewOnly(false);
                          onOpen();
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        borderRadius="full"
                        display="none"
                        onClick={() => {
                          setDeleteTargetId(item._id);
                          onDeleteOpen();
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {list.length === 0 && !loading && (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={20}>
                    <Text color="gray.400" fontStyle="italic">No bank details registered in the master list</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* PAGINATION CONTROLS */}
      <Flex mt={6} align="center" justify="space-between" bg="gray.50" p={3} borderRadius="xl" border="1px" borderColor="gray.100">
        <HStack spacing={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">
            Showing {list.length} of {totalCount} Records
          </Text>
        </HStack>

        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            bg="white"
            isDisabled={page === 1}
            onClick={() => setPage(page - 1)}
            borderRadius="lg"
            _hover={{ bg: "teal.50", color: "teal.600" }}
          >
            Previous
          </Button>

          <HStack spacing={1}>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1))
              .map((p, i, arr) => (
                <Box key={p} display="flex" alignItems="center">
                  {i > 0 && arr[i - 1] !== p - 1 && <Text px={2} color="gray.400">...</Text>}
                  <Button
                    size="sm"
                    variant={page === p ? "solid" : "ghost"}
                    colorScheme={page === p ? "teal" : "gray"}
                    onClick={() => setPage(p)}
                    borderRadius="lg"
                    minW="36px"
                  >
                    {p}
                  </Button>
                </Box>
              ))}
          </HStack>

          <Button
            size="sm"
            variant="outline"
            bg="white"
            isDisabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            borderRadius="lg"
            _hover={{ bg: "teal.50", color: "teal.600" }}
          >
            Next
          </Button>
        </HStack>

        <HStack spacing={3}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500">ROWS</Text>
          <select
            style={{
              fontSize: '12px',
              padding: '4px 8px',
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              backgroundColor: 'white',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </HStack>
      </Flex>

      {/* DRAWER FOR ADD/EDIT/VIEW */}
      <Drawer isOpen={isOpen} onClose={handleClose} placement="right">
        <DrawerOverlay backdropFilter="blur(15px)" bg="blackAlpha.700" />
        <DrawerContent maxW="75%" borderRadius="40px 0 0 40px" boxShadow="-25px 0 60px rgba(0,0,0,0.3)">
          <DrawerCloseButton
            size="lg"
            top="6"
            right="8"
            borderRadius="full"
            bg="whiteAlpha.900"
            _hover={{ bg: "red.50", color: "red.500", transform: "rotate(90deg)" }}
            transition="0.4s"
          />
          <DrawerHeader
            borderBottomWidth="1px"
            bgGradient="linear(to-r, teal.600, green.400)"
            color="white"
            borderTopLeftRadius="40px"
            py={6}
            px={10}
          >
            <Flex align="center" justify="space-between" pr={16}>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="900" letterSpacing="wider">
                  {isViewOnly ? "MASTER DATA PREVIEW" : (editingId ? "UPDATE BANK DETAILS" : "REGISTER FOREIGN BANK")}
                </Text>
                <Text fontSize="xs" fontWeight="bold" opacity="0.8" textTransform="uppercase" mt={1}>
                  {isViewOnly ? "Authorized Read-Only View" : "Strict Regulatory Compliance Entry"}
                </Text>
              </VStack>
              {!isViewOnly && (
                <Badge colorScheme="whiteAlpha" color="white" border="1px solid" borderRadius="full" px={4} py={1} fontSize="2xs">
                  EDITABLE MODE
                </Badge>
              )}
            </Flex>
          </DrawerHeader>

          <DrawerBody py={0} px={0} bg="gray.100">
            <Box overflowY="auto" py={6} px={10} h="full">
                {isViewOnly ? (
              <VStack spacing={6} align="stretch">
                {/* SECTION 1: IDENTITY VIEW */}
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl" borderTop="8px solid" borderTopColor="teal.500">
                  <HStack mb={4} justify="space-between">
                    <HStack spacing={3}>
                      <Box w="10px" h="24px" bg="teal.500" borderRadius="full" />
                      <Text fontSize="md" fontWeight="900" color="gray.800" letterSpacing="tight">
                        IDENTITY & ADDRESS
                      </Text>
                    </HStack>
                    <Badge colorScheme="teal" variant="solid" borderRadius="full" px={4}>
                      {form.vendorCode}
                    </Badge>
                  </HStack>
                  <SimpleGrid columns={1} spacing={6}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">BENEFICIARY NAME</Text>
                      <Text fontSize="md" fontWeight="800" color="teal.800">{form.beneficiaryName}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">COUNTRY OF REGISTRY</Text>
                      <Text fontSize="md" fontWeight="800" color="gray.700">{form.country}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">PHYSICAL ADDRESS</Text>
                      <Text fontSize="md" fontWeight="600" color="gray.700" lineHeight="tall">{form.beneficiaryAddress}</Text>
                    </VStack>
                  </SimpleGrid>
                </Box>

                {/* SECTION 2: BANKING VIEW */}
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl" borderTop="8px solid" borderTopColor="blue.500">
                  <HStack mb={4} spacing={3}>
                    <Box w="10px" h="24px" bg="blue.500" borderRadius="full" />
                    <Text fontSize="md" fontWeight="900" color="gray.800" letterSpacing="tight">
                      FINANCIAL INFRASTRUCTURE
                    </Text>
                  </HStack>
                  <SimpleGrid columns={1} spacing={6}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">BENEFICIARY BANK</Text>
                      <Text fontSize="md" fontWeight="800" color="blue.800">{form.beneficiaryBank}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">ACCOUNT NO</Text>
                      <Text fontSize="lg" fontWeight="900" color="gray.800" fontFamily="mono" bg="gray.50" px={2} borderRadius="md">{form.accountNo}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">SWIFT CODE</Text>
                      <Text fontSize="lg" fontWeight="900" color="gray.800" fontFamily="mono" bg="gray.50" px={2} borderRadius="md">{form.swiftCode}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">IBAN NUMBER</Text>
                      <Text fontSize="md" fontWeight="700" color="gray.700" fontFamily="mono">{form.ibanNumber || "—"}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">ROUTING NO</Text>
                      <Text fontSize="md" fontWeight="700" color="gray.700">{form.routingNumber || "—"}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">ABA NO</Text>
                      <Text fontSize="md" fontWeight="700" color="gray.700">{form.abaNumber || "—"}</Text>
                    </VStack>
                  </SimpleGrid>
                </Box>

                {/* SECTION 3: CORRESPONDING VIEW */}
                {(form.correspondingBankName || form.correspondingAccountNo || form.correspondingSwiftCode) && (
                  <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl" borderTop="8px solid" borderTopColor="teal.200">
                    <HStack mb={4} spacing={3}>
                      <Box w="10px" h="24px" bg="teal.200" borderRadius="full" />
                      <Text fontSize="md" fontWeight="900" color="teal.700" letterSpacing="tight">
                        CORRESPONDING INTERMEDIARY
                      </Text>
                    </HStack>
                    <SimpleGrid columns={1} spacing={6}>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">BANK NAME</Text>
                        <Text fontSize="sm" fontWeight="800" color="gray.700">{form.correspondingBankName}</Text>
                      </VStack>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">ACCOUNT NO</Text>
                        <Text fontSize="sm" fontWeight="800" color="gray.700" fontFamily="mono">{form.correspondingAccountNo}</Text>
                      </VStack>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">SWIFT CODE</Text>
                        <Text fontSize="sm" fontWeight="800" color="gray.700" fontFamily="mono">{form.correspondingSwiftCode}</Text>
                      </VStack>
                      {form.correspondingRoutingNumber && (
                        <VStack align="start" spacing={1}>
                          <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">ROUTING NUMBER</Text>
                          <Text fontSize="sm" fontWeight="800" color="gray.700">{form.correspondingRoutingNumber}</Text>
                        </VStack>
                      )}
                    </SimpleGrid>
                  </Box>
                )}

                {/* AUDIT LOG */}
                <Box p={6} bg="white" borderRadius="2xl" border="1px" borderColor="gray.200" boxShadow="sm">
                  <SimpleGrid columns={1} spacing={4}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">AUTHORIZED BY</Text>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{form.createdBy || "SYSTEM"}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">INITIAL REGISTRY</Text>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{form.createdAt ? new Date(form.createdAt).toLocaleString() : "N/A"}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">LAST AUDIT</Text>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{form.updatedAt ? new Date(form.updatedAt).toLocaleString() : "N/A"}</Text>
                    </VStack>
                  </SimpleGrid>
                </Box>

              </VStack>
            ) : (
              <SimpleGrid columns={1} spacing={6}>
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl" border="2px dashed" borderColor="purple.200">
                  <HStack mb={4} spacing={4}>
                    <Box w="10px" h="24px" bg="purple.500" borderRadius="full" />
                    <Text fontSize="md" fontWeight="900" color="gray.800" letterSpacing="tight">
                      Supporting Documents
                    </Text>
                  </HStack>
                  <VStack align="start" spacing={4} w="full">
                    <HStack w="full">
                      <Input
                        type="file"
                        p={1}
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            const file = e.target.files[0];
                            setDocumentFile(file);
                            const url = URL.createObjectURL(file);
                            setDocumentUrl(url);
                            setDocumentType(file.type);
                          }
                        }}
                      />
                      {documentUrl && (
                        <IconButton
                          aria-label="Preview Local File"
                          icon={<ViewIcon />}
                          colorScheme="teal"
                          onClick={() => setSelectedFile({ open: true, file: null, localUrl: documentUrl, localType: documentType || "application/pdf" })}
                        />
                      )}
                    </HStack>
                  </VStack>
                </Box>

                {/* PRIMARY DETAILS */}
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl">
                  <HStack mb={4} spacing={4}>
                    <Box w="10px" h="24px" bg="teal.500" borderRadius="full" />
                    <Text fontSize="md" fontWeight="900" color="gray.800" letterSpacing="tight">
                      Identification & Entity
                    </Text>
                  </HStack>
                  <VStack align="stretch" spacing={8}>
                    {/* STEP 1: COMPANY SELECTION */}
                    <Box>
                      <CustomInput
                        name="companyCode"
                        label="COMPANY CODE *"
                        type="select"
                        value={form.companyCode}
                        disabled={isViewOnly}
                        onChange={(e: any) => handleChange("companyCode", e)}
                        options={COMPANY_CODES.map(c => ({ label: c, value: c }))}
                      />
                    </Box>

                    <Divider borderColor="gray.100" />

                    {/* STEP 2: SAP VENDOR SEARCH */}
                    <Box bg="gray.50" p={5} borderRadius="xl" border="1px" borderColor="teal.100">
                      <VStack align="start" spacing={4} position="relative">
                        <HStack spacing={2}>
                          <Box w="8px" h="16px" bg="teal.400" borderRadius="full" />
                          <Text fontSize="xs" fontWeight="900" color="teal.700" letterSpacing="wider">SAP VENDOR SEARCH (NAME / CODE)</Text>
                        </HStack>
                        <HStack w="full">
                          <Input
                            placeholder="Enter Name or Code to search in SAP..."
                            value={vendorSearch}
                            onChange={(e) => handleVendorSearchChange(e.target.value)}
                            isDisabled={isViewOnly || !form.companyCode}
                            bg="white"
                            borderRadius="xl"
                            fontSize="sm"
                            fontWeight="bold"
                            boxShadow="sm"
                          />
                          <Button
                            colorScheme="teal"
                            onClick={handleVendorSearch}
                            isLoading={isVendorSearching}
                            isDisabled={isViewOnly || !form.companyCode}
                            borderRadius="xl"
                            px={8}
                          >
                            Search
                          </Button>
                        </HStack>

                        {showVendorList && (
                          <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            bg="white"
                            boxShadow="2xl"
                            borderRadius="xl"
                            zIndex={100}
                            maxH="250px"
                            overflowY="auto"
                            border="1px"
                            borderColor="gray.200"
                            mt={2}
                          >
                            {vendorList.length > 0 ? (
                              vendorList.map((v, i) => (
                                <Box
                                  key={i}
                                  p={4}
                                  _hover={{ bg: "teal.50", cursor: "pointer" }}
                                  onClick={() => selectVendor(v)}
                                  borderBottom="1px"
                                  borderColor="gray.100"
                                >
                                  <Text fontSize="sm" fontWeight="800" color="gray.800">{v.supplierName}</Text>
                                  <Text fontSize="xs" fontWeight="bold" color="teal.600" mt={1}>CODE: {v.vendorCode}</Text>
                                  {v.vendorAddress && <Text fontSize="2xs" color="gray.500" mt={1} isTruncated>{v.vendorAddress}</Text>}
                                </Box>
                              ))
                            ) : (
                              <Box p={4} textAlign="center"><Text fontSize="sm" color="gray.500">No vendors found</Text></Box>
                            )}
                          </Box>
                        )}
                      </VStack>
                    </Box>

                    <Divider borderColor="gray.100" />

                    {/* STEP 3: REFINED ENTITY INFO */}
                    <SimpleGrid columns={1} spacing={6}>
                      <CustomInput
                        name="vendorCode"
                        label="VENDOR CODE"
                        value={form.vendorCode}
                        disabled={true}
                      />
                      <CustomInput
                        name="beneficiaryName"
                        label="BENEFICIARY NAME"
                        value={form.beneficiaryName}
                        disabled={true}
                        isRequired={true}
                      />
                      <CustomInput
                        name="beneficiaryAddress"
                        label="BENEFICIARY ADDRESS"
                        value={form.beneficiaryAddress}
                        disabled={isViewOnly}
                        isRequired={true}
                        onChange={(e: any) => handleChange("beneficiaryAddress", e.target.value)}
                      />
                      <CustomInput
                        name="country"
                        label="COUNTRY"
                        value={form.country}
                        disabled={isViewOnly}
                        isRequired={true}
                        onChange={(e: any) => handleChange("country", e.target.value)}
                      />
                    </SimpleGrid>
                  </VStack>
                </Box>

                {/* BANKING STRUCTURE */}
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl">
                  <HStack mb={4} spacing={4}>
                    <Box w="10px" h="24px" bg="blue.500" borderRadius="full" />
                    <Text fontSize="md" fontWeight="900" color="gray.800" letterSpacing="tight">
                      Beneficiary Bank Infrastructure
                    </Text>
                  </HStack>
                  <SimpleGrid columns={1} spacing={6}>
                    <Box>
                      <CustomInput
                        name="beneficiaryBank"
                        label="BENEFICIARY BANK"
                        value={form.beneficiaryBank}
                        disabled={isViewOnly}
                        isRequired={true}
                        onChange={(e: any) => handleChange("beneficiaryBank", e.target.value)}
                      />
                    </Box>
                    <CustomInput
                      name="accountNo"
                      label="ACCOUNT NO"
                      value={form.accountNo}
                      disabled={isViewOnly}
                      isRequired={true}
                      onChange={(e: any) => handleChange("accountNo", e.target.value)}
                    />
                    <CustomInput
                      name="swiftCode"
                      label="SWIFT CODE"
                      value={form.swiftCode}
                      disabled={isViewOnly}
                      isRequired={true}
                      onChange={(e: any) => handleChange("swiftCode", e.target.value)}
                    />
                    <CustomInput
                      name="ibanNumber"
                      label="IBAN NUMBER"
                      value={form.ibanNumber}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("ibanNumber", e.target.value)}
                    />
                    <CustomInput
                      name="routingNumber"
                      label="ROUTING NUMBER"
                      value={form.routingNumber}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("routingNumber", e.target.value)}
                    />
                    <CustomInput
                      name="abaNumber"
                      label="ABA NUMBER"
                      value={form.abaNumber}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("abaNumber", e.target.value)}
                    />
                  </SimpleGrid>
                </Box>

                {/* CORRESPONDING BANK */}
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl" border="2px dashed" borderColor="teal.100">
                  <HStack mb={4} spacing={4}>
                    <Box w="10px" h="24px" bg="teal.200" borderRadius="full" />
                    <Text fontSize="md" fontWeight="900" color="teal.700" letterSpacing="tight">
                      Corresponding Bank Details
                    </Text>
                  </HStack>
                  <SimpleGrid columns={1} spacing={6}>
                    <CustomInput
                      name="correspondingBankName"
                      label="CORRESPONDING BANK"
                      value={form.correspondingBankName}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("correspondingBankName", e.target.value)}
                    />
                    <CustomInput
                      name="correspondingAccountNo"
                      label="ACCOUNT NO"
                      value={form.correspondingAccountNo}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("correspondingAccountNo", e.target.value)}
                    />
                    <CustomInput
                      name="correspondingSwiftCode"
                      label="SWIFT CODE"
                      value={form.correspondingSwiftCode}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("correspondingSwiftCode", e.target.value)}
                    />
                    <CustomInput
                      name="correspondingRoutingNumber"
                      label="ROUTING NUMBER"
                      value={form.correspondingRoutingNumber}
                      disabled={isViewOnly}
                      onChange={(e: any) => handleChange("correspondingRoutingNumber", e.target.value)}
                    />
                  </SimpleGrid>
                </Box>

              </SimpleGrid>
            )}
            </Box>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" gap={6} py={6} px={10} bg="white">
            <Button variant="ghost" mr={3} onClick={handleClose} borderRadius="full" px={10} size="sm">
              {isViewOnly ? "EXIT VIEW" : "CANCEL ENTRY"}
            </Button>
            {!isViewOnly && (
              <Button
                colorScheme="teal"
                onClick={handleAdd}
                isLoading={loading}
                borderRadius="full"
                px={16}
                h="45px"
                fontSize="sm"
                fontWeight="900"
                boxShadow="0 15px 25px -5px rgba(49, 151, 149, 0.4)"
                _hover={{ transform: "scale(1.02)", boxShadow: "0 20px 30px -5px rgba(49, 151, 149, 0.5)" }}
                _active={{ transform: "scale(0.98)" }}
              >
                {editingId ? "COMMIT CHANGES" : "RETAIN DETAILS"}
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* DELETE CONFIRM MODAL */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(8px)" />
        <ModalContent borderRadius="3xl" boxShadow="dark-lg">
          <ModalHeader fontSize="xl" fontWeight="900">
            CONFIRM DELETE
          </ModalHeader>
          <Divider />
          <ModalBody py={8}>
            <Text fontWeight="600" color="gray.700">
              Are you sure you want to delete this bank registry? This regulatory action cannot be undone.
            </Text>
          </ModalBody>
          <Divider />
          <ModalFooter gap={4} py={6}>
            <Button variant="ghost" onClick={onDeleteClose} borderRadius="full" px={6}>
              CANCEL
            </Button>
            <Button colorScheme="red" onClick={handleDelete} isLoading={loading} borderRadius="full" px={10} fontWeight="800">
              DELETE
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <FileViewerModal
        isOpen={selectedFile.open}
        file={selectedFile.file}
        localUrl={selectedFile.localUrl}
        localType={selectedFile.localType}
        onClose={() => setSelectedFile({ file: null, open: false, localUrl: "", localType: "" })}
      />
    </ShadowBox>
  );
}
